import React, { useState } from 'react';
import { useStore, Order } from '../hooks/useStore';
import { ClockIcon, PackageIcon, CheckCircleIcon, TruckIcon, UsersIcon, PlusIcon, TrashIcon, EyeIcon, EyeOffIcon } from 'lucide-react';
export function DashboardPage() {
  const {
    orders,
    updateOrderStatus,
    adminUsers,
    createAdminUser,
    deleteAdminUser
  } = useStore();
  const [activeTab, setActiveTab] = useState<'orders' | 'users'>('orders');
  const [newUsername, setNewUsername] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [userError, setUserError] = useState('');
  const [userSuccess, setUserSuccess] = useState('');
  const statusConfig: Record<string, {
    color: string;
    bg: string;
    icon: React.ReactNode;
  }> = {
    pendiente: {
      color: 'text-amber-700',
      bg: 'bg-amber-50 border-amber-200',
      icon: <ClockIcon className="w-4 h-4" data-id="element-212" />
    },
    preparando: {
      color: 'text-blue-700',
      bg: 'bg-blue-50 border-blue-200',
      icon: <PackageIcon className="w-4 h-4" data-id="element-213" />
    },
    listo: {
      color: 'text-emerald-700',
      bg: 'bg-emerald-50 border-emerald-200',
      icon: <CheckCircleIcon className="w-4 h-4" data-id="element-214" />
    },
    entregado: {
      color: 'text-stone-500',
      bg: 'bg-stone-50 border-stone-200',
      icon: <TruckIcon className="w-4 h-4" data-id="element-215" />
    }
  };
  const stats = [{
    label: 'Total',
    value: orders.length,
    color: 'bg-black text-white'
  }, {
    label: 'Pendientes',
    value: orders.filter(o => o.status === 'pendiente').length,
    color: 'bg-amber-50 text-amber-700 border border-amber-200'
  }, {
    label: 'En Cocina',
    value: orders.filter(o => o.status === 'preparando').length,
    color: 'bg-blue-50 text-blue-700 border border-blue-200'
  }, {
    label: 'Listos',
    value: orders.filter(o => o.status === 'listo').length,
    color: 'bg-emerald-50 text-emerald-700 border border-emerald-200'
  }];
  const handleCreateUser = (e: React.FormEvent) => {
    e.preventDefault();
    setUserError('');
    setUserSuccess('');
    if (!newUsername.trim() || !newPassword.trim()) {
      setUserError('Completa todos los campos.');
      return;
    }
    if (newPassword.length < 6) {
      setUserError('La contraseña debe tener al menos 6 caracteres.');
      return;
    }
    const ok = createAdminUser(newUsername.trim(), newPassword);
    if (!ok) {
      setUserError('Ese nombre de usuario ya existe.');
    } else {
      setUserSuccess(`Usuario "${newUsername}" creado correctamente.`);
      setNewUsername('');
      setNewPassword('');
    }
  };
  return <div className="min-h-screen bg-stone-50 py-10 px-4" data-id="element-216">
      <div className="max-w-7xl mx-auto" data-id="element-217">
        <div className="flex items-center justify-between mb-8" data-id="element-218">
          <h1 className="font-display text-2xl text-stone-900 font-bold" data-id="element-219">
            Panel de Administración
          </h1>
          <div className="flex gap-2" data-id="element-220">
            <button onClick={() => setActiveTab('orders')} className={`px-4 py-2 rounded-lg text-sm font-bold transition-all ${activeTab === 'orders' ? 'bg-black text-white' : 'bg-white border border-stone-200 text-stone-600 hover:bg-stone-50'}`} data-id="element-221">
              Pedidos
            </button>
            <button onClick={() => setActiveTab('users')} className={`px-4 py-2 rounded-lg text-sm font-bold transition-all flex items-center gap-2 ${activeTab === 'users' ? 'bg-black text-white' : 'bg-white border border-stone-200 text-stone-600 hover:bg-stone-50'}`} data-id="element-222">
              <UsersIcon className="w-4 h-4" data-id="element-223" /> Usuarios
            </button>
          </div>
        </div>

        {activeTab === 'orders' && <>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8" data-id="element-224">
              {stats.map((s, i) => <div key={i} className={`p-5 rounded-xl ${s.color}`} data-id="element-225">
                  <div className="text-3xl font-bold" data-id="element-226">{s.value}</div>
                  <div className="text-xs font-semibold uppercase tracking-wider mt-1 opacity-80" data-id="element-227">
                    {s.label}
                  </div>
                </div>)}
            </div>

            <div className="bg-white border border-stone-200 rounded-xl overflow-hidden shadow-sm" data-id="element-228">
              <table className="w-full text-left" data-id="element-229">
                <thead className="bg-stone-50 border-b border-stone-200" data-id="element-230">
                  <tr data-id="element-231">
                    {['ID', 'Cliente', 'Items', 'Total', 'Estado', 'Acción'].map(h => <th key={h} className="p-4 text-xs font-semibold uppercase text-stone-400 tracking-wider" data-id="element-232">
                        {h}
                      </th>)}
                  </tr>
                </thead>
                <tbody className="divide-y divide-stone-100" data-id="element-233">
                  {orders.length === 0 ? <tr data-id="element-234">
                      <td colSpan={6} className="p-12 text-center text-stone-400" data-id="element-235">
                        No hay pedidos activos.
                      </td>
                    </tr> : orders.map(order => <tr key={order.id} className="hover:bg-stone-50/50" data-id="element-236">
                        <td className="p-4 font-mono text-xs text-stone-400" data-id="element-237">
                          #{order.id.slice(0, 5)}
                        </td>
                        <td className="p-4" data-id="element-238">
                          <div className="font-semibold text-sm" data-id="element-239">
                            {order.customerName}
                          </div>
                          <div className="text-xs text-stone-400" data-id="element-240">
                            {order.customerPhone} · {order.type}
                          </div>
                        </td>
                        <td className="p-4 text-sm text-stone-600 max-w-xs" data-id="element-241">
                          {order.items.map(i => `${i.quantity}× ${i.menuItem.name}`).join(', ')}
                        </td>
                        <td className="p-4 font-bold text-sm" data-id="element-242">
                          RD$ {order.total.toFixed(2)}
                        </td>
                        <td className="p-4" data-id="element-243">
                          <span className={`inline-flex items-center gap-1.5 text-xs font-bold uppercase px-2.5 py-1 rounded-full border ${statusConfig[order.status]?.bg} ${statusConfig[order.status]?.color}`} data-id="element-244">
                            {statusConfig[order.status]?.icon} {order.status}
                          </span>
                        </td>
                        <td className="p-4" data-id="element-245">
                          <select value={order.status} onChange={e => updateOrderStatus(order.id, e.target.value as Order['status'])} className="text-xs border border-stone-200 rounded-lg p-2 bg-white focus:ring-2 focus:ring-black/10 outline-none cursor-pointer" data-id="element-246">
                            <option value="pendiente" data-id="element-247">Pendiente</option>
                            <option value="preparando" data-id="element-248">Preparando</option>
                            <option value="listo" data-id="element-249">Listo</option>
                            <option value="entregado" data-id="element-250">Entregado</option>
                          </select>
                        </td>
                      </tr>)}
                </tbody>
              </table>
            </div>
          </>}

        {activeTab === 'users' && <div className="grid grid-cols-1 md:grid-cols-2 gap-8" data-id="element-251">
            {/* Create user */}
            <div className="bg-white border border-stone-200 rounded-xl p-8 shadow-sm" data-id="element-252">
              <h2 className="font-display text-lg font-bold text-stone-900 mb-6" data-id="element-253">
                Crear Usuario Admin
              </h2>
              {userError && <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-600 text-xs font-bold" data-id="element-254">
                  {userError}
                </div>}
              {userSuccess && <div className="mb-4 p-3 bg-emerald-50 border border-emerald-200 rounded-lg text-emerald-700 text-xs font-bold" data-id="element-255">
                  {userSuccess}
                </div>}
              <form onSubmit={handleCreateUser} className="space-y-4" data-id="element-256">
                <div data-id="element-257">
                  <label className="text-xs font-bold uppercase tracking-widest text-stone-400 mb-2 block" data-id="element-258">
                    Usuario
                  </label>
                  <input type="text" value={newUsername} onChange={e => {
                setNewUsername(e.target.value);
                setUserError('');
                setUserSuccess('');
              }} className="w-full p-3.5 bg-stone-50 border border-stone-200 rounded-xl text-sm font-medium outline-none focus:border-black transition-all" placeholder="nombre_usuario" data-id="element-259" />
                </div>
                <div data-id="element-260">
                  <label className="text-xs font-bold uppercase tracking-widest text-stone-400 mb-2 block" data-id="element-261">
                    Contraseña
                  </label>
                  <div className="relative" data-id="element-262">
                    <input type={showPassword ? 'text' : 'password'} value={newPassword} onChange={e => {
                  setNewPassword(e.target.value);
                  setUserError('');
                  setUserSuccess('');
                }} className="w-full p-3.5 pr-12 bg-stone-50 border border-stone-200 rounded-xl text-sm font-medium outline-none focus:border-black transition-all" placeholder="Mínimo 6 caracteres" data-id="element-263" />
                    <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-3 top-1/2 -translate-y-1/2 text-stone-400 hover:text-stone-700" data-id="element-264">
                      {showPassword ? <EyeOffIcon className="w-4 h-4" data-id="element-265" /> : <EyeIcon className="w-4 h-4" data-id="element-266" />}
                    </button>
                  </div>
                </div>
                <button type="submit" className="w-full py-3.5 bg-black text-white font-bold rounded-xl hover:bg-stone-800 transition-colors flex items-center justify-center gap-2 text-sm" data-id="element-267">
                  <PlusIcon className="w-4 h-4" data-id="element-268" /> Crear Usuario
                </button>
              </form>
            </div>

            {/* User list */}
            <div className="bg-white border border-stone-200 rounded-xl p-8 shadow-sm" data-id="element-269">
              <h2 className="font-display text-lg font-bold text-stone-900 mb-6" data-id="element-270">
                Usuarios Activos{' '}
                <span className="text-stone-400 font-normal text-sm" data-id="element-271">
                  ({adminUsers.length})
                </span>
              </h2>
              <div className="space-y-3" data-id="element-272">
                {adminUsers.map(user => <div key={user.id} className="flex items-center justify-between p-4 bg-stone-50 rounded-xl border border-stone-100" data-id="element-273">
                    <div data-id="element-274">
                      <p className="font-bold text-sm text-stone-900" data-id="element-275">
                        {user.username}
                      </p>
                      <p className="text-xs text-stone-400 font-medium" data-id="element-276">
                        Administrador
                      </p>
                    </div>
                    {adminUsers.length > 1 && <button onClick={() => deleteAdminUser(user.id)} className="p-2 text-stone-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors" title="Eliminar usuario" data-id="element-277">
                        <TrashIcon className="w-4 h-4" data-id="element-278" />
                      </button>}
                  </div>)}
              </div>
              <p className="text-xs text-stone-400 mt-4 font-medium" data-id="element-279">
                * No puedes eliminar el último usuario administrador.
              </p>
            </div>
          </div>}
      </div>
    </div>;
}